"use client";

import { faShare } from "@fortawesome/pro-solid-svg-icons/faShare";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Text, TextInput } from "@mantine/core";
import Image from "next/image";
import Link from "next/link";
import { type FormEvent, useCallback, useState } from "react";

import { userService } from "@/app/api/user";
import { AuthFormContainer } from "@/components/auth-form-container";

export default function Forgot() {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [serverError, setServerError] = useState("");

  const onSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      setServerError("");

      // Check if the given input vaguely resembles an email. This is not an exhaustive, full-featured regex, just used
      // to alleviate common/simple errors
      if (email.match(/^\S+@\S+\.\S+$/)) {
        userService
          .forgotPassword(email)
          .then(() => {
            setResetSent(true);
            setEmailError("");
          })
          .catch(() => {
            setServerError(
              "There was an issue communicating with the server. Please try again.",
            );
          });
      } else {
        setEmailError("Please enter a valid email");
      }
    },
    [email],
  );

  return resetSent ? (
    <>
      <Text size="lg" fw={600}>
        Reset Your Password
      </Text>
      <div className="w-80 flex flex-col justify-center items-center text-center">
        <Image
          alt="Envelope"
          className="my-4"
          height={60}
          src="/assets/envelope.svg"
          width={60}
        />
        <Text size="md">
          If your email is in our system, instructions for resetting your
          password have been sent to {email || "your email"}.
        </Text>
        <div className="mt-4">
          <Text size="sm">
            <Link className="font-semibold text-uw-purple" href="/auth/login">
              Log In
            </Link>
          </Text>
        </div>
      </div>
    </>
  ) : (
    <AuthFormContainer
      bottomContent={
        <Text size="sm">
          <Link className="font-semibold text-uw-purple" href="/auth/login">
            Log In
          </Link>
        </Text>
      }
      errorMessage={serverError}
      onSubmit={onSubmit}
      submitButtonContent="Send Reset Link"
      submitButtonProps={{ rightSection: <FontAwesomeIcon icon={faShare} /> }}
      title="Reset Your Password"
    >
      <TextInput
        className="my-2 w-full"
        error={emailError}
        onChange={(e) => setEmail(e.target.value.toLowerCase())}
        placeholder="Email Address"
      />
    </AuthFormContainer>
  );
}
